import { forwardRef } from 'react'

import { tv } from 'tailwind-variants'

import { Icon } from '@/components/atoms'

export const selectVariants = tv({
  slots: {
    containerSlot: 'relative',
    selectSlot:
      'text-surface-white-400 placeholder:text-surface-white-400 border-surface-white-300 focus:border-surface-white-400 focus:ring-surface-white-400 bg-surface-white-50 font-body w-full cursor-pointer appearance-none rounded-sm border px-4 pr-10 font-normal outline-none ring-0',
    iconSlot:
      'text-surface-white-400 pointer-events-none absolute bottom-0 right-3.5 top-0 flex items-center justify-center'
  },
  variants: {
    size: {
      sm: {
        selectSlot: 'text-caption-sm h-10 py-2'
      },
      md: {
        selectSlot: 'text-body-md h-12 py-3'
      },
      lg: {
        selectSlot: 'text-body-lg h-14 py-4'
      }
    },
    error: {
      true: {
        selectSlot:
          'border-danger-100 text-danger-200 focus:border-danger-200 focus:ring-danger-100 font-semibold focus:ring-1',
        iconSlot: 'text-danger-200'
      }
    },
    disabled: {
      true: {
        selectSlot:
          'bg-surface-white-200 text-surface-white-400 border-surface-white-300 cursor-not-allowed opacity-60',
        iconSlot: 'text-surface-white-400 opacity-60'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

export interface TSelectOption {
  label: string
  value: string | number
}

export type TSelectProps = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> & {
  options: TSelectOption[]
  error?: string
  placeholder?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  helperTextId?: string
  errorTextId?: string
}

/**
 * @deprecated Use `Select`/`SelectTrigger`/`SelectContent`/`SelectItem` from `@alternative/acqua/molecules` instead. The acqua version is a Base UI compound component, not a native `<select>`.
 */
export const Select = forwardRef<HTMLSelectElement, TSelectProps>(
  (
    {
      options,
      placeholder,
      className,
      size,
      error,
      disabled,
      helperTextId,
      errorTextId,
      ...props
    },
    ref
  ) => {
    const { name, id, ...restProps } = props

    const describedBy =
      [helperTextId, errorTextId].filter(Boolean).join(' ') || undefined

    const { containerSlot, selectSlot, iconSlot } = selectVariants({
      size,
      error: !!error,
      disabled: !!disabled
    })

    return (
      <div className={containerSlot()}>
        <select
          className={selectSlot({ className })}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          ref={ref}
          name={name}
          id={id || name}
          {...restProps}
        >
          {placeholder && (
            <option value='' disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className={iconSlot()}>
          <Icon name='keyboard_arrow_down' size='md' />
        </div>
      </div>
    )
  }
)

Select.displayName = 'Select'
